/**
 * First-paint instrumentation for the client-onboarding wizard.
 *
 * The auth → onboarding handoff fans through several blocking gates
 * (TokenHandler → AuthGuard → AppContent → useOnboardingApplication).
 * When users report "the page took a minute to load" we want a precise
 * answer instead of a guess. `markPhase(name)` writes a `performance.mark`
 * AND a structured client-log line, so:
 *
 *   - DevTools → Performance shows the named markers on the timeline
 *     and `performance.getEntriesByName(name)[0].startTime` is millisecond-
 *     accurate from `performance.timeOrigin`.
 *   - Browser console (or Vercel runtime logs if the user copies them)
 *     shows the structured `[FirstPaint]` lines with the same timestamp,
 *     letting us correlate offline.
 *
 * Each phase name fires at most once per page load — duplicate calls are
 * silently no-op so a re-render or React StrictMode double-invoke doesn't
 * produce noise.
 */

import { createClientLogger } from '@order-up/client/vite';

const logger = createClientLogger('FirstPaint');

const fired = new Set<string>();

/**
 * Phase identifiers used at the call sites. Pinned as a union so a typo
 * fails the type-check rather than emitting a stray marker.
 */
export type FirstPaintPhase =
  | 'app-shell-mount'
  | 'token-handoff-start'
  | 'token-handoff-complete'
  | 'auth-guard-pass'
  | 'application-loaded'
  | 'first-render';

/**
 * Mark a first-paint phase. Safe to call from any component — guarded
 * against SSR (no `performance` on the server) and against duplicate
 * invocations.
 */
export function markPhase(name: FirstPaintPhase): void {
  if (typeof performance === 'undefined' || typeof window === 'undefined') {
    return;
  }
  if (fired.has(name)) return;
  fired.add(name);

  try {
    performance.mark(name);
  } catch {
    // performance.mark may throw on a corrupted entry table — never break
    // the host call site.
  }

  // `performance.timeOrigin` lets us compute a wall-clock offset later.
  // We log relative-from-timeOrigin so the value is comparable across
  // sessions without exposing the user's clock.
  const offsetMs = Math.round(performance.now());
  logger.info('phase', { phase: name, offsetMs });
}

/**
 * Resets the per-page-load fired set. Test-only.
 */
export function __resetFirstPaintForTests(): void {
  fired.clear();
}
